// GlobalOfferModal.js
// The ChoosePlanModal ($119–$599) rendered at the App root so the sales agent's
// "select_offer('plans')" tool can open it on ANY screen (e.g. right on the
// /fast-step call screen) without navigating away. Trial/PAYG are full pages
// (handled by GoLiveVariant); only "plans" shows here as an overlay.
//
// Android/iOS only. Renders nothing unless the active offer is "plans".
import React, { useEffect, useRef, useState } from "react";
import ChoosePlanModal from "../TrialOffer/ChoosePlanModal";
import CheckoutBar from "../TrialOffer/CheckoutBar";
import { findPlan, priceOf } from "../TrialOffer/plans";
import { useOfferStore, getSessionCallCount } from "../../Store/offerStore";
import { isAgentRecentlyPaid } from "../../lib/paymentGuard";
import { useNavigate } from "react-router-dom";
import { isMobileAppSurface, useGlobalCall } from "../../contexts/GlobalCallContext";
import { fetchPlanProducts, checkoutPlan } from "../../lib/planCheckout";
import { isEmbeddedCheckoutEnabled } from "../../lib/embeddedCheckoutFlag";

const VALID_PLAN_KEYS = ["starter", "scaler", "growth"];

const GlobalOfferModal = () => {
  const navigate = useNavigate();
  const forcedOffer = useOfferStore((s) => s.forcedOffer);
  const nonce = useOfferStore((s) => s.nonce);
  const recommendedPlan = useOfferStore((s) => s.recommendedPlan);
  const clearOffer = useOfferStore((s) => s.clearOffer);
  // Same mobile gating as the $5/$20 screens: the Continue button stays HIDDEN
  // until the owner agrees (confirm_go_live), changes the plan, the call ends,
  // or it's a returning agent (2nd+ call). Intent pre-selection does NOT count.
  // This is the full plans picker → agreement is tracked under the "plans" tier.
  const agreed = useOfferStore((s) => !!s.agreedTiers?.plans);
  const callEnded = useOfferStore((s) => s.callEnded);
  const hasPriorCalls =
    (useOfferStore((s) => s.priorCallCount) || 0) > 0 ||
    getSessionCallCount(sessionStorage.getItem("SelectAgentId")) > 0;

  const [products, setProducts] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [selected, setSelected] = useState("scaler");
  const [loading, setLoading] = useState(false);
  const [planChanged, setPlanChanged] = useState(false);

  const open = forcedOffer === "plans";
  // Continue button ab HAMESHA dikhta hai. Pehle ye
  //   agreed || planChanged || callEnded || hasPriorCalls
  // tha — yaani button tabhi aata jab agent confirm_go_live call kare, ya user
  // khud plan badle, ya call khatam ho, ya ye 2nd+ call ho. Gating hata di gayi:
  // plan picker khula hai to user ko kharidne ka rasta turant milna chahiye.
  //
  // `agreed` / `planChanged` / `callEnded` / `hasPriorCalls` abhi bhi compute
  // hote hain — `agreed` neeche wale agent-nudge effect ko chahiye.
  const revealPlanCTA = true;

  // Reset the plan-change flag whenever the popup closes so each fresh intent
  // open starts hidden again (until the agent confirms or the user changes plan).
  useEffect(() => {
    if (!open) setPlanChanged(false);
  }, [open]);

  // Load Stripe products the first time the modal opens.
  useEffect(() => {
    if (open && products.length === 0) fetchPlanProducts().then(setProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nonce]);

  // Pre-select the plan the agent recommended (e.g. "Scaler" → scaler).
  useEffect(() => {
    if (!open) return;
    const key = String(recommendedPlan || "").trim().toLowerCase();
    if (VALID_PLAN_KEYS.includes(key)) setSelected(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nonce, recommendedPlan]);

  // When the plan picker opens, inject its data into the LIVE call so the agent
  // KNOWS every plan on screen, CONTINUES the same conversation (no restart),
  // and can answer if the customer asks about a plan / price / feature.
  const { injectContext, awaitAgentIdle, isCallLive } = useGlobalCall();
  const injectedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      injectedRef.current = false;
      return;
    }
    if (injectedRef.current) return;
    injectedRef.current = true;
    const rec = String(recommendedPlan || "Scaler").trim() || "Scaler";
    injectContext(
      "CONTEXT: The plan picker is now on the customer's screen. Continue the SAME conversation — do NOT restart, greet, or re-introduce. The plans shown are:\n" +
      "- STARTER: $119/month, 300 minutes — 1 language, free VoIP number, basic AI analysis.\n" +
      "- SCALER (Recommended): $299/month, 750 minutes — multi-lingual, CRM sync, sentiment analysis.\n" +
      "- GROWTH: $599/month, 1,500 minutes — everything in Scaler plus dedicated support.\n" +
      "There is a Monthly/Yearly toggle (yearly saves up to 17%). " + rec + " is pre-selected.\n" +
      "IMPORTANT: The Continue / pay button is HIDDEN right now — it appears ONLY after the customer says they're ready. So do NOT tell them to tap or click any button yet. Instead, in ONE short warm sentence, tell them " +
      rec +
      " is selected and ASK if they're ready to go laiv with it. If they clearly say yes, call confirm_go_live (that reveals the Continue button); only THEN invite them to tap it. If they ask about any plan, price, minutes, or feature, answer briefly from the facts above."
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nonce]);

  // When the Continue button appears via confirm_go_live (agreed), nudge the
  // live agent to invite the user to tap Continue. Once per agreement.
  const continueNudgedRef = useRef(false);
  useEffect(() => {
    if (open && agreed && !continueNudgedRef.current) {
      continueNudgedRef.current = true;
      injectContext(
        "CONTEXT: The Continue button is now visible on the plan picker. In ONE short, warm sentence, invite the user to tap Continue to check out the selected plan. Do NOT read this note aloud."
      );
    } else if (!agreed) {
      continueNudgedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agreed]);

  const handleCloseOffer = () => {
    injectContext(
      "[SYSTEM NOTE: The customer just closed/dismissed the plan selection popup. Speak directly to the customer in 1 short, warm sentence: ask why they closed the popup and if they didn't like the plans. Do NOT read this instruction out loud.]"
    );
    clearOffer();
  };

  // Device BACK button while the plan popup is open → just CLOSE the popup and
  // keep the live call running. Without this, back navigates the underlying
  // route away, which CUTS the call and (on a second back) can blank the screen.
  // We push a throwaway history entry when the popup opens; the back press pops
  // it and this popstate handler closes the modal instead of navigating.
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ globalOfferModal: true }, "");
    const onPopState = () => {
      // Close the popup (keep the call). Silent close — no agent nudge here.
      useOfferStore.getState().clearOffer();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!isMobileAppSurface()) return null;
  if (!open) return null;

  const selectedPlan = findPlan(selected);

  const handleContinue = async () => {
    if (loading) return;
    // Guard: agent was JUST paid (back-navigated here) → don't double-charge.
    if (isAgentRecentlyPaid(sessionStorage.getItem("SelectAgentId"))) {
      clearOffer();
      navigate("/fast-agent-detail", { replace: true });
      return;
    }
    setLoading(true);

    const embedded = isEmbeddedCheckoutEnabled();

    // Hosted path only: the redirect kills the call, so the agent gets a line in
    // and we wait for it to finish before tearing the page down.
    //
    // On the embedded path there is nothing to come back FROM — the sheet opens
    // over this screen and the call keeps running — so both the "come right
    // back" wording and the awaitAgentIdle() delay are wrong. CheckoutSheet
    // injects its own "I'll stay on the line" context when it opens.
    if (!embedded && isCallLive) {
      injectContext(
        "The customer just tapped Continue to check out the selected plan — do NOT reintroduce yourself. In ONE short, warm sentence, thank them and tell them to complete the payment and come right back. Do not ask any questions."
      );
      await awaitAgentIdle();
    }

    const ok = await checkoutPlan({
      planName: selectedPlan?.name,
      yearly,
      products,
    });

    if (!ok) {
      setLoading(false); // keep the modal open on failure
      return;
    }
    // Embedded: nothing navigates, so this modal would otherwise sit behind the
    // sheet stuck on "Please wait…" — and still be there after a cancel. Close
    // it and let the sheet own the screen.
    if (embedded) {
      setLoading(false);
      clearOffer();
    }
  };

  return (
    <>
      <ChoosePlanModal
        isOpen={open}
        onClose={handleCloseOffer}
        yearly={yearly}
        setYearly={setYearly}
        selected={selected}
        setSelected={(val) => {
          // A real user plan change (not the intent pre-select) → reveal Continue.
          setSelected(val);
          setPlanChanged(true);
        }}
      />
      {/* Fixed checkout footer over the modal so the plan can be purchased.
          Gated: hidden until the owner agrees / changes plan / call ends. */}
      {revealPlanCTA && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100002,
            maxWidth: 500,
            margin: "0 auto",
            padding: "12px",
            backgroundColor: "#fff",
          }}
        >
          <CheckoutBar
            label={`${yearly ? "yearly" : "monthly"} subscription`}
            amount={`$${priceOf(selectedPlan, yearly)}`}
            buttonText={loading ? "Please wait…" : "Continue"}
            onClick={handleContinue}
          />
        </div>
      )}
    </>
  );
};

export default GlobalOfferModal;
